import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { adminClient } from './data/adminClient';

// Lets the admin tick which printouts they need and get one combined PDF.
// The list of printouts comes from the backend (backend/printReports.js),
// so adding a new one there makes it appear here with no frontend change.
export default function PrintDialog({ open, onClose }) {
  const [reports, setReports] = useState(null);
  const [schools, setSchools] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [school, setSchool] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSchool('');
    setError(null);
    Promise.all([adminClient.getPrintReports(), adminClient.getEventRegistrations()])
      .then(([reportList, registrations]) => {
        setReports(reportList);
        // Withdrawn schools are left out of every printout server-side too.
        // Test schools are left out of "All schools" server-side, but can
        // still be picked here to try a printout on them. Schools still
        // pending their registration payment never print, so aren't listed.
        setSchools(registrations
          .filter(r => r.status !== 'Withdrawn' && r.schoolName && !r.paymentPending)
          .map(r => ({ name: r.schoolName, isTest: !!r.isTest }))
          .sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(err => setError(err.message));
  }, [open]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const chosen = useMemo(() => (reports || []).filter(r => selected.has(r.id)), [reports, selected]);
  const showSchoolFilter = chosen.some(r => r.supportsSchoolFilter);

  const handlePrint = () => {
    // Keep the dialog's listed order so the PDF reads the same way every time.
    adminClient.openPrintPdf(chosen.map(r => r.id), showSchoolFilter ? school : '');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Print</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Choose what you need printed. Everything you tick comes out as one PDF, in the order listed. Withdrawn and test schools are left out of “All schools”, and schools whose registration payment hasn't been received are left out entirely.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!reports && !error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(reports || []).map(r => (
              <FormControlLabel
                key={r.id}
                sx={{ alignItems: 'flex-start', m: 0 }}
                control={<Checkbox checked={selected.has(r.id)} onChange={() => toggle(r.id)} sx={{ pt: 0.25 }} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{r.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.description}</Typography>
                  </Box>
                }
              />
            ))}
          </Box>
        )}

        {showSchoolFilter && (
          <FormControl size="small" fullWidth sx={{ mt: 3 }}>
            <InputLabel>School</InputLabel>
            <Select label="School" value={school} onChange={e => setSchool(e.target.value)}>
              <MenuItem value="">All schools</MenuItem>
              {schools.map(({ name, isTest }) => <MenuItem key={name} value={name}>{name}{isTest ? ' (test school)' : ''}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={chosen.length === 0}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
